import { HttpErrorResponse } from '@angular/common/http';
import { InjectionToken, inject } from '@angular/core';
import {
  ChannelUsage,
  DeviceModel,
  MeterRole,
  MeterRoleOrigin,
  PartnerModel,
  PvForecastingConfig,
} from '@core/models';
import {
  InstallationAttributesService,
  PhaseCountReading,
} from '@core/services/installation-attributes.service';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  EMPTY,
  Observable,
  catchError,
  map,
  merge,
  mergeMap,
  of,
  pipe,
  tap,
  throwError,
} from 'rxjs';

// A 403 is told apart from any other failure: the value exists, the user's role is not shown it.
export type AttributeRead<T> =
  | { state: 'loading' }
  | { state: 'loaded'; value: T }
  | { state: 'notShared' }
  | { state: 'failed' };

export interface DeviceAttributeReads {
  device: AttributeRead<DeviceModel>;
  phaseCount: AttributeRead<PhaseCountReading>;
  // A loaded null is a device without a forecast config. Stays loading where it is not read.
  pvConfig: AttributeRead<PvForecastingConfig | null>;
  channelUsage: AttributeRead<ChannelUsage>;
}

type InstallationAttributesState = {
  devices: Record<number, DeviceAttributeReads>;
  // Keyed by lowercase partner oid.
  partners: Record<string, AttributeRead<PartnerModel>>;
};

// Where the role a device applies comes from, or why none could be worked out.
export type ResolvedMeterRole =
  | { state: 'resolved'; role: MeterRole; origin: MeterRoleOrigin }
  | { state: 'loading' }
  | { state: 'notShared' | 'failed' };

// The role the device page applies: the device's own, else its partner's default, else
// bidirectional.
export function resolveMeterRole(
  read: AttributeRead<DeviceModel>,
  partners: Record<string, AttributeRead<PartnerModel>>,
): ResolvedMeterRole {
  if (read.state !== 'loaded') return read.state === 'loading' ? { state: 'loading' } : read;

  const device = read.value;
  if (device.meterRole) return { state: 'resolved', role: device.meterRole, origin: 'device' };
  if (!device.partnerOid) return { state: 'resolved', role: 'bidirectional', origin: 'fallback' };

  const partner = partners[device.partnerOid.toLowerCase()];
  if (!partner || partner.state === 'loading') return { state: 'loading' };
  if (partner.state !== 'loaded') return partner;

  const partnerDefault = partner.value.partnerDefaultMeterRole;
  return partnerDefault
    ? { state: 'resolved', role: partnerDefault, origin: 'partnerDefault' }
    : { state: 'resolved', role: 'bidirectional', origin: 'fallback' };
}

const LOADING = { state: 'loading' } as const;

const LOADING_DEVICE: DeviceAttributeReads = {
  device: LOADING,
  phaseCount: LOADING,
  pvConfig: LOADING,
  channelUsage: LOADING,
};

function read<T>(request: Observable<T>): Observable<AttributeRead<T>> {
  return request.pipe(
    map(value => ({ state: 'loaded', value }) as AttributeRead<T>),
    catchError((error: unknown) =>
      of<AttributeRead<T>>(
        error instanceof HttpErrorResponse && error.status === 403
          ? { state: 'notShared' }
          : { state: 'failed' },
      ),
    ),
  );
}

function absentOn404<T>(request: Observable<T>): Observable<T | null> {
  return request.pipe(
    catchError((error: unknown) =>
      error instanceof HttpErrorResponse && error.status === 404
        ? of(null)
        : throwError(() => error),
    ),
  );
}

// The General tab shows no PV forecast, so it provides this as false and its store does not read
// the forecast configs.
export const READS_PV_CONFIG = new InjectionToken<boolean>('READS_PV_CONFIG', {
  factory: () => true,
});

// Provided by the installation General and Settings tabs, so it reads only while one of them is
// shown and holds one installation's devices. A device that feeds several points is read once.
export const InstallationAttributesStore = signalStore(
  withState<InstallationAttributesState>({
    devices: {},
    partners: {},
  }),
  withMethods((store, service = inject(InstallationAttributesService)) => {
    const readsPvConfig = inject(READS_PV_CONFIG);

    const patchDevice = (deviceId: number, change: Partial<DeviceAttributeReads>): void =>
      patchState(store, state => ({
        devices: { ...state.devices, [deviceId]: { ...state.devices[deviceId], ...change } },
      }));

    // The partner is only asked for its default meter role, which matters to a device that stores
    // none of its own.
    const partnerFor = (
      device: AttributeRead<DeviceModel>,
      reload: boolean,
    ): Observable<unknown> => {
      if (device.state !== 'loaded' || device.value.meterRole || !device.value.partnerOid) {
        return EMPTY;
      }
      const partnerOid = device.value.partnerOid.toLowerCase();
      if (store.partners()[partnerOid] && !reload) return EMPTY;

      // A reload keeps showing the default it has until the new one arrives.
      if (!reload) {
        patchState(store, state => ({ partners: { ...state.partners, [partnerOid]: LOADING } }));
      }
      return read(service.partner(partnerOid)).pipe(
        tap(partner =>
          patchState(store, state => ({ partners: { ...state.partners, [partnerOid]: partner } })),
        ),
      );
    };

    const readsFor = (deviceId: number, reload = false): Observable<unknown>[] => [
      read(service.device(deviceId)).pipe(
        tap(device => patchDevice(deviceId, { device })),
        mergeMap(device => partnerFor(device, reload)),
      ),
      read(service.phaseCount(deviceId)).pipe(
        tap(phaseCount => patchDevice(deviceId, { phaseCount })),
      ),
      ...(readsPvConfig
        ? [
            read(absentOn404(service.pvConfig(deviceId))).pipe(
              tap(pvConfig => patchDevice(deviceId, { pvConfig })),
            ),
          ]
        : []),
      read(service.channelUsage(deviceId)).pipe(
        tap(channelUsage => patchDevice(deviceId, { channelUsage })),
      ),
    ];

    return {
      // Reactive where it is called from a computed: it reads the device and partner signals.
      meterRoleOf: (deviceId: number): ResolvedMeterRole =>
        resolveMeterRole(store.devices()[deviceId]?.device ?? LOADING, store.partners()),
      // Shows a forecast config the device was just given, as the write answered it.
      setPvConfig: (deviceId: number, value: PvForecastingConfig): void =>
        patchDevice(deviceId, { pvConfig: { state: 'loaded', value } }),
      load: rxMethod<number[]>(
        pipe(
          map(deviceIds => [...new Set(deviceIds)].filter(deviceId => !store.devices()[deviceId])),
          tap(deviceIds =>
            patchState(store, state => ({
              devices: {
                ...state.devices,
                ...Object.fromEntries(deviceIds.map(deviceId => [deviceId, LOADING_DEVICE])),
              },
            })),
          ),
          mergeMap(deviceIds => merge(...deviceIds.flatMap(deviceId => readsFor(deviceId)))),
        ),
      ),
      // Reads a device's values again after one of them was written. The old values stay on show
      // until the new ones arrive, so the card does not collapse and grow back.
      reloadDevice: rxMethod<number>(mergeMap(deviceId => merge(...readsFor(deviceId, true)))),
    };
  }),
);
